/**
 * AssignmentFeedTable — 「전체 테스트」의 여덟 칸 표.
 *
 * 얼굴은 구 테스트관리 화면(`src/pages/ExamList.js`)의 표를 그대로 옮겼다(오너 지시 2026-09-06
 * 「구형 페이지와 비슷할 것」) — 열 순서·비율 폭·두 줄 셀·동그란 학생 번호까지 같다.
 * 값만 v2 배정의 것이다. 두 칸이 갈린다:
 *   과목 → **시험지 제목**   신규 시험지는 교육과정 종속을 없앤 것이 정의라 대신할 값이 제목뿐이다
 *   TID  → **배정 번호**     `α·δ·𝐂` 기호와 「(n회)」는 뺐다. `exam_type` 은 서버가 리포트
 *                            판별자로 쓰는 값이고 입력도 2026-09-05 부터 잠겼다
 *
 * 난이도는 구 표와 **같은 값·같은 표기**다 — 서버가 100 배 정수로 주고(2026-09-06 회신 §2 4-6·4-7,
 * `188` = 1.88) 100 으로 나눠 소수 둘째 자리까지 적는다. 그날 오전까지는 스케일을 몰라 빈칸으로
 * 두었는데, 「모른다」를 `2.1` 로 적는 것보다 빈칸이 낫다는 판단이었다. 회신이 오면서 켰다.
 *
 * ■ 정답률은 마감분에만 그린다(오너 결정 2026-09-06)
 * 구 화면은 총문항이 1 이상이면 **언제나** 백분율을 그리고 색도 늘 초록이었다
 * (`ExamList.js:775`·`:854`). 그래서 아무도 채점하지 않은 시험이 초록 「0%」로 서고, 전부 틀린
 * 시험도 같은 초록 「0%」로 섰다. 이 표는 배정된 것을 전부 세므로 채점 전 배정이 대부분을
 * 차지한다 — 그 규칙을 옮기면 첫날부터 「0%」를 스무 번 넘게 말하게 된다.
 * 아랫줄의 「정답/오답/총」은 늘 보이므로 진행 상황은 그대로 읽힌다.
 *
 * 값의 규칙(마감 판정·집계·정답률·시각 표기)은 새로 만들지 않고 `examPaperApi` 의 헬퍼를 쓴다.
 * 같은 값이 시험지 화면과 이 화면에서 다른 수로 보이면, 그때부터 둘 다 믿지 못한다.
 */
import { CheckCircle2, PlayCircle, FilePlus } from 'lucide-react';
import {
  assignmentCounts, accuracyOf, accuracyTone, isAssignmentClosed, assignmentDifficulty,
  accuracyBasisText, accuracyEmptyText,
  fmtServerDateTime, fmtServerDateShort, studentSchoolText, studentClassText,
} from '../ExamPapersV2/examPaperApi';

/** 예약(아직 열리기 전)인가 — 계약 §1-2 의 status 50 */
const isScheduled = (r) => r?.status === 50;

/**
 * 상태 아이콘 — 구 표의 세 아이콘을 그대로 쓰되 v2 의 세 상태에 붙인다.
 * 구 화면은 아이콘에 설명이 없어 색만으로 뜻을 짐작해야 했다. 여기서는 `title` 을 단다.
 */
function StatusIcon({ row }) {
  if (isAssignmentClosed(row)) {
    return <CheckCircle2 className="text-green-600" size={24} aria-hidden="true" />;
  }
  if (isScheduled(row)) {
    return <FilePlus className="text-blue-500" size={24} aria-hidden="true" />;
  }
  return <PlayCircle className="text-orange-500" size={24} aria-hidden="true" />;
}

/** 상태를 글자로 — 구 표의 「(n회)」가 있던 보조줄 자리다 */
function statusText(row) {
  if (isAssignmentClosed(row)) return '마감';
  if (isScheduled(row)) {
    const at = fmtServerDateShort(row?.open_at);
    return at ? `${at} 오픈` : '예약';
  }
  return '진행 중';
}

/** 시각 두 줄 — 「출제」는 늘, 「마감」은 지난 일이면 실제 시각, 아니면 걸려 있는 예정 */
function endText(row) {
  if (row?.closed_at) return fmtServerDateTime(row.closed_at);
  if (row?.close_at) return `~${fmtServerDateTime(row.close_at)} 예정`;
  return null;
}

/**
 * @param {Array} rows      배정 행(서버가 준 그대로 + 시험지 값 셋)
 * @param {(assignmentId:number)=>void} onOpen  행을 누르면 채점 창을 여는 자
 * @param {boolean} tight   좁은 화면에서 셀 좌우 여백을 줄인다(학생 표와 같은 자)
 */
export default function AssignmentFeedTable({ rows = [], onOpen, tight = false }) {
  const pad = tight ? 'px-1' : 'px-1.5';

  return (
    <table className="w-full table-fixed" style={{ minWidth: '450px' }}>
      {/* 폭은 구 표의 비율을 그대로 옮겼다 — 합이 100% 다 */}
      <colgroup>
        <col style={{ width: '19%' }} />{/* 학생 */}
        <col style={{ width: '11%' }} />{/* 담당·강의 */}
        <col style={{ width: '16%' }} />{/* 시험지 */}
        <col style={{ width: '15%' }} />{/* 상태·번호 */}
        <col style={{ width: '7%' }} /> {/* 난이도 */}
        <col style={{ width: '6%' }} /> {/* 문제수 */}
        <col style={{ width: '8%' }} /> {/* 정답률 */}
        <col style={{ width: '18%' }} />{/* 일시 */}
      </colgroup>
      <thead className="bg-gray-50">
        <tr>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-center text-xs font-medium text-gray-500`}>학생</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-left text-xs font-medium text-gray-500`}>담당/강의</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-left text-xs font-medium text-gray-500`}>시험지</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-center text-xs font-medium text-gray-500`}>상태/번호</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-center text-xs font-medium text-gray-500`}>난이도</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-center text-xs font-medium text-gray-500`}>문제수</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-center text-xs font-medium text-gray-500`}>정답률</th>
          <th scope="col" className={`${pad} whitespace-nowrap py-2 text-left text-xs font-medium text-gray-500`}>일시</th>
        </tr>
      </thead>
      <tbody className="bg-white">
        {rows.map((r, i) => {
          const aid = r?.assignment_id ?? null;
          const cnt = assignmentCounts(r);
          const rate = accuracyOf(r);
          const difficulty = assignmentDifficulty(r);
          const school = studentSchoolText(r);
          const klass = studentClassText(r);
          const open = aid != null && onOpen ? () => onOpen(aid) : undefined;
          const end = endText(r);
          // 배정 시점 판이 지금 시험지의 판과 갈렸는가 — 시험지 이름 옆에 조용히 말한다.
          // 현재 판(paper_current_revision)은 조립이 시험지 목록에서 붙인 값이고, 서버 목록이
          // 열리면 그쪽이 실어 준다(요청서 §2-3). 없으면 이 안내만 빠진다.
          const cur = r?.paper_current_revision;
          const rev = r?.paper_revision;
          const staleRev = cur != null && rev != null && String(cur) !== String(rev);

          return (
            <tr
              key={String(aid ?? `row-${i}`)}
              onClick={open}
              className={`border-b border-gray-100 transition-colors duration-150 ${
                aid == null ? 'opacity-50' : 'cursor-pointer hover:bg-primary/10'
              }`}
            >
              {/* 학생 — 동그란 번호 + 이름, 아랫줄에 학교·학년 */}
              <td className={`${pad} overflow-hidden rounded-l-3xl py-2 transition-colors duration-150`}>
                <div className="flex min-w-0 items-center">
                  <div className="size-8 shrink-0">
                    <div
                      className="flex size-8 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white"
                      title={r?.student_id != null ? `학생 ID: ${r.student_id}` : '학생 정보 없음'}
                    >
                      {r?.student_id ?? '-'}
                    </div>
                  </div>
                  <div className="ml-2 min-w-0">
                    <div className="truncate text-sm font-medium leading-tight text-gray-900">
                      {r?.student_name || '(이름 없음)'}
                    </div>
                    {/* 학생이 삭제됐으면 신상 6필드가 통째로 null 로 온다(2026-09-06 회신 §1) —
                        빈 줄 대신 '-' 를 적어 「값이 없다」와 「안 그려졌다」를 가른다 */}
                    <div className="truncate text-xs leading-tight text-gray-500">{school || '-'}</div>
                  </div>
                </div>
              </td>

              {/* 담당·강의 */}
              <td className={`${pad} overflow-hidden py-2 transition-colors duration-150`}>
                <div className="truncate text-sm text-gray-900">{klass || '-'}</div>
              </td>

              {/* 시험지 — 구 표의 「과목」 자리 */}
              <td className={`${pad} overflow-hidden py-2 transition-colors duration-150`}>
                <div
                  className="truncate text-sm leading-tight text-gray-900"
                  title={r?.paper_title || undefined}
                >
                  {r?.paper_title || '-'}
                </div>
                {staleRev && (
                  <div
                    className="truncate text-xs leading-tight text-gray-400"
                    title={`배포 시점 판 rev ${rev} — 지금 시험지는 rev ${cur} 입니다`}
                  >
                    rev {rev} (옛 판)
                  </div>
                )}
              </td>

              {/* 상태·번호 — 구 표의 아이콘 + 번호 + 보조줄 */}
              <td className={`${pad} overflow-hidden py-2 text-center transition-colors duration-150`}>
                <div className="flex items-center justify-center gap-1">
                  <div className="shrink-0" title={statusText(r)}>
                    <StatusIcon row={r} />
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-gray-800">{aid != null ? aid : '-'}</div>
                    <div className="text-xs text-gray-500">{statusText(r)}</div>
                  </div>
                </div>
              </td>

              {/* 난이도 — 구 표와 같은 자리·같은 글자다(`ExamList.js:843`). 값이 없으면 `-` */}
              <td className={`${pad} py-2 text-center transition-colors duration-150`}>
                <span className="text-sm font-semibold text-gray-800">{difficulty ?? '-'}</span>
              </td>

              {/* 문제수 */}
              <td className={`${pad} py-2 text-center transition-colors duration-150`}>
                <span className="text-sm font-semibold text-gray-800">{cnt.total ?? '-'}</span>
              </td>

              {/* 정답률 — 마감분에만. 아랫줄의 정답/오답/총은 늘 보인다.
                  분모는 아랫줄의 「총」이 아니라 채점한 문항(정답+오답)이다 — 셈의 근거와 「-」의
                  이유를 title 로 말한다. 문장은 시험지 화면과 같은 것을 쓴다(examPaperApi) */}
              <td className={`${pad} py-2 text-center transition-colors duration-150`}>
                <div className={`text-sm font-semibold ${rate != null ? accuracyTone(rate) : 'text-gray-400'}`}>
                  {rate != null
                    ? <span title={accuracyBasisText(cnt.correct, cnt.wrong, cnt.total)}>{rate}%</span>
                    : <span title={accuracyEmptyText(r)}>-</span>}
                </div>
                <div className="text-[10px]">
                  <span className="font-bold text-primary">{cnt.correct ?? 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="font-bold text-danger">{cnt.wrong ?? 0}</span>
                  <span className="text-gray-400">/</span>
                  <span className="font-bold text-black">{cnt.total ?? 0}</span>
                </div>
              </td>

              {/* 일시 — 구 표의 생성/수정 자리에 출제/마감 */}
              <td className={`${pad} overflow-hidden rounded-r-3xl py-2 transition-colors duration-150`}>
                <div className="text-xs text-gray-500">
                  <div className="truncate">출제: {fmtServerDateTime(r?.assigned_at) || '-'}</div>
                  <div className="truncate">마감: {end || '-'}</div>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}
